import type { AnalyticsEmployeeSummary, AnalyticsSummary, ApprovedHours, DecisionTimeMetric, DecisionTimeMetrics } from '@gestion-permisos/contracts';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import { type FormEvent, Fragment, type ReactNode, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { useApi } from '../api/api-context.js';
import { ApiProblem } from '../api/problem.js';
import {
  type AnalyticsFilters,
  analyticsQuery,
  useAnalyticsFilters,
  useAnalyticsSummary,
  useApprovedHours,
  useAreaOptions,
  useDecisionTimes,
  useTeamOptions,
} from '../analytics/analytics-api.js';
import { saveBlob } from '../analytics/save-file.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { ChoiceList } from '../components/choice-list.js';
import { Card, CARD_SURFACE, CardContent, CardHeader, CardTitle } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { ProblemMessage } from '../components/problem-message.js';
import { usePermissionTypes } from '../requests/catalog-api.js';
import { formatHours, formatInstant, STATUS_LABELS } from '../requests/format.js';
import { STATUS_OPTIONS, TABLE, TD, TH } from '../requests/request-list.js';
import { useMe } from '../session/session.js';

const INPUT = 'mt-1 block w-full rounded-md border border-input bg-card px-2 py-1.5 text-sm';
/** Tablas y recuadros dentro de las tarjetas, con los tokens de `styles.css`. */
const TABLE_BOX = 'overflow-x-auto rounded-lg border border-border';
const TABLE_HEAD = 'border-b border-border bg-muted text-muted-foreground';
const ROW = 'border-t border-border first:border-t-0';
const STAT_BOX = 'rounded-lg border border-border bg-background p-4';
/** Verde de la marca para las barras (`--chart-1` es `--brand`). */
const BAR_FILL = 'var(--chart-1)';
/** Orden alfabético de los nombres al ordenar la lista de empleados. */
const NAME_COLLATOR = new Intl.Collator('es');

/** `2030-03` → `03/2030`, como texto. */
const formatMonth = (month: string) => (/^\d{4}-\d{2}$/.test(month) ? `${month.slice(5)}/${month.slice(0, 4)}` : month);
const formatPercent = (value: number) => `${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(value)} %`;

const BUCKET_LABELS: Record<DecisionTimeMetrics['histogram'][number]['key'], string> = {
  lt1h: '< 1 h',
  h1to4: '1–4 h',
  h4to8: '4–8 h',
  h8to24: '8–24 h',
  d1to2: '1–2 días',
  d2to3: '2–3 días',
  gt3d: '> 3 días',
};

/**
 * Analítica (W6): `summary`, `approved-hours` y `decision-times` con los mismos filtros, y la exportación a
 * Excel que genera la API. **Ningún cálculo en el cliente**: se muestran los valores tal como llegan; las horas
 * de cada empleado son `byEmployee[].approvedHours` del servidor y su detalle por mes, las filas de
 * `approved-hours`. Ordenar y filtrar la lista de empleados es solo presentación. Los tiempos de decisión son
 * **horas naturales** de reloj (D-08 abierta: sin escalamiento). El ámbito lo decide la API: un 403 (empleado,
 * o Director General sin los roles de Responsable o Administrador) reemplaza la pantalla.
 */
/** 400 de la API, para distinguirlo de un fallo de red o de un 403. */
function badRequest(error: unknown): ApiProblem | null {
  return error instanceof ApiProblem && error.status === 400 ? error : null;
}

export function AnalyticsPage() {
  const [filters, apply] = useAnalyticsFilters();
  const summary = useAnalyticsSummary(filters);
  const hours = useApprovedHours(filters);
  const times = useDecisionTimes(filters);
  const draft = useFilterDraft(filters, apply);

  const errors = [summary.error, hours.error, times.error].filter((error) => error !== null);
  const denied = errors.find((error) => error instanceof ApiProblem && error.status === 403);
  if (denied) return <ApiErrorView error={denied} />;
  // Un 400 de fechas o del resto de filtros oculta las secciones: esos campos siguen en la tarjeta de arriba.
  // Un 400 solo del umbral no: el campo vive en «Tiempos de decisión» y tiene que poder corregirse ahí.
  const filtersInvalid = badRequest(summary.error) ?? badRequest(hours.error);
  const thresholdInvalid = filtersInvalid === null ? badRequest(times.error) : null;

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <PageHeader title="Analítica" description="Consulta las solicitudes y las horas aprobadas de cada empleado. Las cifras las calcula el servidor." />
      <Card>
        <CardContent className="space-y-4">
          <FilterForm draft={draft} />
          <ExportButton filters={filters} />
        </CardContent>
      </Card>
      {filtersInvalid !== null ? (
        <ProblemMessage error={filtersInvalid} />
      ) : (
        <>
          {summary.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
          {summary.isError ? <ApiErrorView error={summary.error} /> : null}
          {summary.data !== undefined && !summary.isError ? (
            <>
              <Totals summary={summary.data} />
              <EmployeesCard employees={summary.data.byEmployee} hours={hours} />
              <Card>
                <CardHeader>
                  <CardTitle>Distribución</CardTitle>
                </CardHeader>
                <CardContent>
                  <DistributionView summary={summary.data} />
                </CardContent>
              </Card>
            </>
          ) : null}
          <Card>
            <CardHeader>
              <CardTitle>Tiempos de decisión</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <ThresholdForm draft={draft} />
              {thresholdInvalid !== null ? <ProblemMessage error={thresholdInvalid} /> : null}
              {times.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
              {times.isError && thresholdInvalid === null ? <ApiErrorView error={times.error} /> : null}
              {times.data !== undefined && !times.isError ? <DecisionTimesView metrics={times.data} /> : null}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

interface FilterDraft {
  readonly values: AnalyticsFilters;
  readonly set: (name: keyof AnalyticsFilters, value: string) => void;
  /** Escribe el borrador completo (filtros y umbral) en la URL: lo usan los dos «Aplicar filtros». */
  readonly submit: (event: FormEvent) => void;
  readonly clear: () => void;
}

/**
 * Borrador de los filtros, compartido por la tarjeta de filtros y la del umbral. Vuelve a los de la URL cuando
 * esta cambia (aplicar, quitar o navegar) sin desmontar la pantalla, así que el orden y las filas desplegadas
 * de la lista de empleados se conservan.
 */
function useFilterDraft(filters: AnalyticsFilters, apply: (next: AnalyticsFilters) => void): FilterDraft {
  const key = JSON.stringify(filters);
  const [draft, setDraft] = useState({ key, values: filters });
  const values = draft.key === key ? draft.values : filters;
  return {
    values,
    set: (name, value) => setDraft({ key, values: { ...values, [name]: value } }),
    submit: (event) => {
      event.preventDefault();
      apply(values);
    },
    clear: () => apply({}),
  };
}

/**
 * Filtros. Tipo para todos (catálogo); área solo para el Administrador (`GET /areas`); empleado solo para un
 * Responsable que no es Administrador (él y su equipo de `/me/team`). El Administrador busca por texto. El
 * umbral de pendientes está en la tarjeta de tiempos de decisión.
 */
function FilterForm({ draft }: { draft: FilterDraft }) {
  const me = useMe();
  const isAdministrator = me.roles.includes('ADMINISTRADOR');
  const isManager = me.roles.includes('RESPONSABLE') && !isAdministrator;
  const types = usePermissionTypes();
  const areas = useAreaOptions(isAdministrator);
  const team = useTeamOptions(isManager);
  const { values, set } = draft;

  return (
    <form onSubmit={draft.submit} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Field id="f-desde" label="Desde">
        <input id="f-desde" type="date" value={values.desde ?? ''} onChange={(event) => set('desde', event.target.value)} className={INPUT} />
      </Field>
      <Field id="f-hasta" label="Hasta">
        <input id="f-hasta" type="date" value={values.hasta ?? ''} onChange={(event) => set('hasta', event.target.value)} className={INPUT} />
      </Field>
      <Field id="f-estado" label="Estado">
        <ChoiceList id="f-estado" value={values.estado ?? ''} options={STATUS_OPTIONS} onChange={(value) => set('estado', value)} className={INPUT} />
      </Field>
      <Field id="f-tipo" label="Tipo de permiso">
        <ChoiceList
          id="f-tipo"
          value={values.tipo ?? ''}
          options={[{ value: '', label: 'Todos' }, ...(types.data?.items.map((type) => ({ value: type.id, label: type.name })) ?? [])]}
          onChange={(value) => set('tipo', value)}
          className={INPUT}
        />
      </Field>
      {isAdministrator ? (
        <Field id="f-area" label="Área">
          <ChoiceList
            id="f-area"
            value={values.area ?? ''}
            options={[{ value: '', label: 'Todas' }, ...(areas.data?.items.map((area) => ({ value: area.id, label: area.name })) ?? [])]}
            onChange={(value) => set('area', value)}
            className={INPUT}
          />
        </Field>
      ) : null}
      {isManager ? (
        <Field id="f-empleado" label="Empleado">
          <ChoiceList
            id="f-empleado"
            value={values.empleado ?? ''}
            options={[
              { value: '', label: 'Todos' },
              { value: me.id, label: me.fullName },
              ...(team.data?.items.map((person) => ({ value: person.id, label: person.fullName })) ?? []),
            ]}
            onChange={(value) => set('empleado', value)}
            className={INPUT}
          />
        </Field>
      ) : null}
      <Field id="f-q" label="Buscar">
        <input id="f-q" type="search" value={values.q ?? ''} onChange={(event) => set('q', event.target.value)} className={INPUT} />
      </Field>
      <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-4">
        <button type="submit" className={buttonClasses()}>
          Aplicar filtros
        </button>
        <button type="button" className={buttonClasses('outline')} onClick={draft.clear}>
          Quitar filtros
        </button>
      </div>
    </form>
  );
}

/** Umbral de pendientes: va en la URL (`umbral`) con los demás filtros y solo se envía a `decision-times`. */
function ThresholdForm({ draft }: { draft: FilterDraft }) {
  return (
    <form onSubmit={draft.submit} className="flex flex-wrap items-end gap-3">
      <Field id="f-umbral" label="Umbral de pendientes (horas naturales)">
        <input
          id="f-umbral"
          inputMode="decimal"
          placeholder="24"
          value={draft.values.umbral ?? ''}
          onChange={(event) => draft.set('umbral', event.target.value)}
          className={INPUT}
        />
      </Field>
      <button type="submit" className={buttonClasses()}>
        Aplicar filtros
      </button>
    </form>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}

/** Descarga el `.xlsx` que genera la API, con la misma query; el nombre sale de `Content-Disposition`. */
function ExportButton({ filters }: { filters: AnalyticsFilters }) {
  const api = useApi();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const download = () => {
    setPending(true);
    setError(null);
    api
      .download(`/api/v1/analytics/approved-hours/export${analyticsQuery(filters)}`)
      .then(({ blob, fileName }) => saveBlob(blob, fileName ?? 'horas-permiso-aprobadas.xlsx'))
      .catch((failure: unknown) => setError(failure))
      .finally(() => setPending(false));
  };
  return (
    <div className="space-y-3 border-t border-border pt-4">
      <button type="button" className={buttonClasses('outline')} disabled={pending} onClick={download}>
        Descargar Excel de horas aprobadas
      </button>
      {error !== null ? error instanceof ApiProblem && error.status === 403 ? <ApiErrorView error={error} /> : <ProblemMessage error={error} /> : null}
    </div>
  );
}

/** Las dos cifras del resumen, tal como llegan: `approvedHours` no es la suma de las personas. */
function Totals({ summary }: { summary: AnalyticsSummary }) {
  return (
    <dl className="grid gap-4 sm:grid-cols-2">
      <div className={`${CARD_SURFACE} p-5`}>
        <dt className="text-sm text-muted-foreground">Solicitudes</dt>
        <dd className="text-2xl font-semibold">{summary.total}</dd>
      </div>
      <div className={`${CARD_SURFACE} p-5`}>
        <dt className="text-sm text-muted-foreground">Horas aprobadas</dt>
        <dd className="text-2xl font-semibold">{formatHours(summary.approvedHours)}</dd>
      </div>
    </dl>
  );
}

type SortKey = 'name' | 'count' | 'approvedHours';
type SortDirection = 'ascending' | 'descending';
interface Sort {
  readonly key: SortKey;
  readonly direction: SortDirection;
}

const SORT_COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'Empleado' },
  { key: 'count', label: 'Solicitudes' },
  { key: 'approvedHours', label: 'Horas aprobadas' },
];

/** Sin mayúsculas ni tildes: «Filtrar esta lista» encuentra «María» escribiendo «maria». */
const searchable = (text: string) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase('es');

/** Orden de presentación de las filas ya cargadas; sin orden, el de la API. Los empates conservan el de la API. */
function sortEmployees(employees: readonly AnalyticsEmployeeSummary[], sort: Sort | null): AnalyticsEmployeeSummary[] {
  if (sort === null) return [...employees];
  const sign = sort.direction === 'descending' ? -1 : 1;
  return [...employees].sort((first, second) =>
    sort.key === 'name' ? sign * NAME_COLLATOR.compare(first.name, second.name) : sign * (first[sort.key] - second[sort.key]),
  );
}

interface HoursResult {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: ApprovedHours[] | undefined;
}

/**
 * «Por empleado»: una fila por persona de `byEmployee`, con sus solicitudes y sus horas aprobadas tal como
 * llegan. Cada fila se despliega con sus meses de `approved-hours`. Ordenar y «Filtrar esta lista» solo
 * reordenan u ocultan filas ya cargadas: no llaman a la API ni cambian la URL.
 */
function EmployeesCard({ employees, hours }: { employees: AnalyticsEmployeeSummary[]; hours: HoursResult }) {
  const [sort, setSort] = useState<Sort | null>(null);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const needle = searchable(filter.trim());
  const visible = sortEmployees(employees, sort).filter((employee) => needle === '' || searchable(employee.name).includes(needle));
  // Primer clic: el nombre de la A a la Z y las cifras de mayor a menor; un segundo clic invierte.
  const toggleSort = (key: SortKey) =>
    setSort((current) =>
      current?.key === key
        ? { key, direction: current.direction === 'descending' ? 'ascending' : 'descending' }
        : { key, direction: key === 'name' ? 'ascending' : 'descending' },
    );
  const toggleRow = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <Card flush>
      <div className="space-y-4 border-b border-border p-5">
        <div className="space-y-1.5">
          <CardTitle>Por empleado</CardTitle>
          <p className="text-sm text-muted-foreground">Las solicitudes respetan los filtros, incluido el estado. Las horas son solo de las aprobadas.</p>
        </div>
        {employees.length > 0 ? (
          <div className="max-w-sm">
            <Field id="f-lista" label="Filtrar esta lista">
              <input id="f-lista" type="search" value={filter} onChange={(event) => setFilter(event.target.value)} className={INPUT} />
            </Field>
          </div>
        ) : null}
        {hours.isError ? <ApiErrorView error={hours.error} /> : null}
      </div>
      {employees.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-muted-foreground">No hay empleados con estos filtros.</p>
      ) : visible.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-muted-foreground">Ningún empleado de la lista coincide con «{filter.trim()}».</p>
      ) : (
        <div className="overflow-x-auto">
          <table className={`${TABLE} min-w-[32rem]`} aria-label="Por empleado">
            <thead className={TABLE_HEAD}>
              <tr>
                {SORT_COLUMNS.map((column) => {
                  const direction = sort?.key === column.key ? sort.direction : undefined;
                  return (
                    <th key={column.key} scope="col" className={TH} aria-sort={direction}>
                      <button type="button" className="inline-flex items-center gap-1 font-medium hover:text-foreground" onClick={() => toggleSort(column.key)}>
                        {column.label}
                        <SortIcon direction={direction} />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visible.map((employee) => {
                const open = expanded.has(employee.id);
                const detailId = `meses-${employee.id}`;
                return (
                  <Fragment key={employee.id}>
                    <tr className={ROW}>
                      <th scope="row" className={TH}>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 text-left font-medium hover:underline"
                          aria-expanded={open}
                          aria-controls={open ? detailId : undefined}
                          onClick={() => toggleRow(employee.id)}
                        >
                          {open ? <ChevronDown aria-hidden className="size-4 shrink-0" /> : <ChevronRight aria-hidden className="size-4 shrink-0" />}
                          {employee.name}
                        </button>
                      </th>
                      <td className={TD}>{employee.count}</td>
                      <td className={TD}>{formatHours(employee.approvedHours)}</td>
                    </tr>
                    {open ? (
                      <tr id={detailId} className="bg-muted/40">
                        <td colSpan={3} className="px-3 pt-1 pb-4 pl-9">
                          <EmployeeMonths employee={employee} hours={hours} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function SortIcon({ direction }: { direction: SortDirection | undefined }) {
  if (direction === 'ascending') return <ArrowUp aria-hidden className="size-3.5" />;
  if (direction === 'descending') return <ArrowDown aria-hidden className="size-3.5" />;
  return <ArrowUpDown aria-hidden className="size-3.5 opacity-50" />;
}

/** Meses de una persona tal como los trae `approved-hours` (ya redondeados por el servidor), sin sumarlos. */
function EmployeeMonths({ employee, hours }: { employee: AnalyticsEmployeeSummary; hours: HoursResult }) {
  if (hours.isPending) return <output className="block text-muted-foreground">Cargando…</output>;
  if (hours.isError || hours.data === undefined) return <p className="text-muted-foreground">No se pudieron cargar los meses.</p>;
  const months = hours.data.filter((row) => row.employeeId === employee.id);
  if (months.length === 0) return <p className="text-muted-foreground">Sin horas aprobadas en este periodo.</p>;
  return (
    <table className={`${TABLE} max-w-xs`} aria-label={`Horas aprobadas de ${employee.name} por mes`}>
      <thead className="text-muted-foreground">
        <tr>
          <th scope="col" className={TH}>Mes</th>
          <th scope="col" className={TH}>Horas</th>
        </tr>
      </thead>
      <tbody>
        {months.map((row) => (
          <tr key={row.month} className={ROW}>
            <th scope="row" className={`${TH} font-normal`}>{formatMonth(row.month)}</th>
            <td className={TD}>{formatHours(row.hours)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CountTable({ caption, head, rows }: { caption: string; head: string; rows: [string, number][] }) {
  if (rows.length === 0) return <p className="text-muted-foreground">{caption}: sin datos.</p>;
  return (
    <div className={TABLE_BOX}>
      <table className={TABLE} aria-label={caption}>
        <thead className={TABLE_HEAD}>
          <tr>
            <th scope="col" className={TH}>{head}</th>
            <th scope="col" className={TH}>Solicitudes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([name, count], index) => (
            <tr key={`${name}-${index}`} className={ROW}>
              <th scope="row" className={TH}>{name}</th>
              <td className={TD}>{count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Gráfico de barras decorativo (los datos están en la tabla de al lado). Tamaño fijo: sin medir el DOM. */
function CountChart({ rows, height = 220 }: { rows: [string, number][]; height?: number }) {
  if (rows.length === 0) return null;
  return (
    <div aria-hidden="true" className="overflow-x-auto">
      <BarChart width={560} height={height} data={rows.map(([name, count]) => ({ name, count }))}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="count" name="Solicitudes" fill={BAR_FILL} />
      </BarChart>
    </div>
  );
}

/** Distribución del resumen, con menos peso: estado y mes con tabla y gráfico; tipo y área, solo en tabla. */
function DistributionView({ summary }: { summary: AnalyticsSummary }) {
  const byStatus: [string, number][] = summary.byStatus.map((item) => [STATUS_LABELS[item.status], item.count]);
  const byMonth: [string, number][] = summary.byMonth.map((item) => [formatMonth(item.month), item.count]);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-3">
        <CountTable caption="Por estado" head="Estado" rows={byStatus} />
        <CountChart rows={byStatus} height={180} />
      </div>
      <div className="space-y-3">
        <CountTable caption="Por mes" head="Mes" rows={byMonth} />
        <CountChart rows={byMonth} height={180} />
      </div>
      <CountTable caption="Por tipo de permiso" head="Tipo" rows={summary.byPermissionType.map((item) => [item.name, item.count])} />
      <CountTable caption="Por área" head="Área" rows={summary.byArea.map((item) => [item.name, item.count])} />
    </div>
  );
}

function DecisionTimesView({ metrics }: { metrics: DecisionTimeMetrics }) {
  const metricRows: [string, DecisionTimeMetric][] = [
    ['Todas', metrics.overall],
    ['Aprobadas', metrics.approved],
    ['Rechazadas', metrics.rejected],
  ];
  const pending = metrics.pendingOverThreshold;
  const histogram: [string, number][] = metrics.histogram.map((bucket) => [BUCKET_LABELS[bucket.key], bucket.count]);
  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Horas naturales de reloj, no hábiles. Medido el {formatInstant(metrics.measuredAt)} (hora de Colombia).
      </p>
      <div className={TABLE_BOX}>
        <table className={TABLE} aria-label="Tiempo hasta la decisión (horas naturales)">
          <thead className={TABLE_HEAD}>
            <tr>
              <th scope="col" className={TH}>Decisiones</th>
              <th scope="col" className={TH}>Promedio (horas naturales)</th>
              <th scope="col" className={TH}>Con tiempo válido</th>
              <th scope="col" className={TH}>Elegibles</th>
              <th scope="col" className={TH}>Cobertura</th>
            </tr>
          </thead>
          <tbody>
            {metricRows.map(([label, metric]) => (
              <tr key={label} className={ROW}>
                <th scope="row" className={TH}>{label}</th>
                <td className={TD}>{metric.averageHours === null ? '—' : formatHours(metric.averageHours)}</td>
                <td className={TD}>{metric.validCount}</td>
                <td className={TD}>{metric.eligibleCount}</td>
                <td className={TD}>{formatPercent(metric.coveragePercent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-3">
        <CountTable caption="Distribución del tiempo hasta la decisión (horas naturales)" head="Tramo" rows={histogram} />
        <CountChart rows={histogram} />
      </div>
      <dl className={`grid gap-3 sm:grid-cols-2 ${STAT_BOX}`}>
        <div>
          <dt className="text-sm text-muted-foreground">Pendientes por encima del umbral (horas naturales)</dt>
          <dd className="text-2xl font-semibold">{pending.count}</dd>
        </div>
        <div>
          <dt className="text-sm text-muted-foreground">Umbral</dt>
          <dd>{formatHours(pending.thresholdHours)} naturales</dd>
        </div>
        <div>
          <dt className="text-sm text-muted-foreground">Pendientes</dt>
          <dd>{pending.totalPending}</dd>
        </div>
        <div>
          <dt className="text-sm text-muted-foreground">Con fecha de envío</dt>
          <dd>
            {pending.withSubmissionCount} ({formatPercent(pending.coveragePercent)})
          </dd>
        </div>
      </dl>
    </div>
  );
}
